// src/components/WebinarRegistrationModal.ts
//
// Modale légère et générique d'inscription à un webinaire.
// Une seule instance pour la page : on l'ouvre via .open(webinar) en passant
// un Webinar issu de src/data/webinars.ts. La modale soumet à
// `${VITE_API_URL}/api/webinar/register` (cryptoaccounting-api).
// Aucun lien Zoom n'est traité côté front : il est envoyé par email via Brevo.

import type { Webinar } from '../data/webinars';
import { loadRecaptchaScript } from '../utils/recaptcha';

const API_URL =
  import.meta.env.VITE_API_URL ||
  'https://forms.cryptoaccounting.fr';

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface WebinarRegistrationPayload {
  webinarId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  position: string;
  recaptchaToken: string;
}

class WebinarRegistrationModal {
  private overlay!: HTMLDivElement;
  private form!: HTMLFormElement;
  private titleEl!: HTMLElement;
  private dateEl!: HTMLElement;
  private audienceEl!: HTMLElement;
  private webinarIdInput!: HTMLInputElement;
  private submitBtn!: HTMLButtonElement;
  private feedbackEl!: HTMLElement;
  private recaptchaWidgetId: number | null = null;
  private isSubmitting = false;

  // La modale est créée au chargement de /webinaires : reCAPTCHA n'est chargé qu'à son ouverture
  constructor() {
    this.build();
    this.attachEvents();
  }

  private build(): void {
    this.overlay = document.createElement('div');
    this.overlay.id = 'webinar-registration-modal';
    this.overlay.className =
      'fixed inset-0 z-50 hidden items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto';
    this.overlay.style.display = 'none';

    this.overlay.innerHTML = `
      <div class="contact-glass relative w-full max-w-lg my-8 max-h-[90vh] overflow-y-auto">
        <button type="button" data-action="close"
          class="absolute top-3 right-3 z-10 bg-white hover:bg-gray-50 rounded-full p-2 transition hover:scale-110"
          style="box-shadow: 3px 3px 8px rgba(19, 58, 201, 0.12), -2px -2px 6px rgba(255, 255, 255, 0.9);"
          aria-label="Fermer">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div class="contact-glass__panel p-6 md:p-8" style="border-radius: 1.5rem;">
          <div class="text-center mb-6">
            <span class="inline-block bg-gradient-to-r from-[#133AC9] to-[#497BF8] text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
              Inscription au webinaire
            </span>
            <h2 data-el="title" class="text-2xl md:text-3xl font-black text-gray-900 leading-tight mb-2"></h2>
            <p class="text-sm text-gray-600">
              <span data-el="date" class="font-semibold text-[#133AC9]"></span>
              · <span data-el="audience"></span>
            </p>
          </div>

          <form data-el="form" class="space-y-3" novalidate>
            <input type="hidden" name="webinarId" data-el="webinar-id" />

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-semibold text-gray-700 mb-1" for="wrm-firstName">Prénom *</label>
                <input id="wrm-firstName" name="firstName" type="text" required
                  class="contact-glass__input"
                  placeholder="Votre prénom" />
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-700 mb-1" for="wrm-lastName">Nom *</label>
                <input id="wrm-lastName" name="lastName" type="text" required
                  class="contact-glass__input"
                  placeholder="Votre nom" />
              </div>
            </div>

            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1" for="wrm-email">Email professionnel *</label>
              <input id="wrm-email" name="email" type="email" required
                class="contact-glass__input"
                placeholder="vous@entreprise.fr" />
            </div>

            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1" for="wrm-phone">Téléphone</label>
              <input id="wrm-phone" name="phone" type="tel"
                class="contact-glass__input"
                placeholder="+33 6 12 34 56 78" />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-semibold text-gray-700 mb-1" for="wrm-company">Entreprise *</label>
                <input id="wrm-company" name="company" type="text" required
                  class="contact-glass__input"
                  placeholder="Nom de votre entreprise" />
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-700 mb-1" for="wrm-position">Fonction *</label>
                <input id="wrm-position" name="position" type="text" required
                  class="contact-glass__input"
                  placeholder="DAF, Expert-comptable, CEO..." />
              </div>
            </div>

            <div class="flex justify-center pt-2">
              <div data-el="recaptcha"></div>
            </div>

            <div data-el="feedback" class="hidden text-sm rounded-lg px-3 py-2"></div>

            <button type="submit" data-el="submit" class="contact-glass__submit w-full">
              Je m'inscris gratuitement
            </button>

            <p class="text-[11px] text-gray-500 text-center pt-1">
              En vous inscrivant, vous acceptez de recevoir par email le lien Zoom et un rappel avant le webinaire.
            </p>
          </form>
        </div>
      </div>
    `;

    document.body.appendChild(this.overlay);

    this.form = this.overlay.querySelector('[data-el="form"]') as HTMLFormElement;
    this.titleEl = this.overlay.querySelector('[data-el="title"]') as HTMLElement;
    this.dateEl = this.overlay.querySelector('[data-el="date"]') as HTMLElement;
    this.audienceEl = this.overlay.querySelector('[data-el="audience"]') as HTMLElement;
    this.webinarIdInput = this.overlay.querySelector('[data-el="webinar-id"]') as HTMLInputElement;
    this.submitBtn = this.overlay.querySelector('[data-el="submit"]') as HTMLButtonElement;
    this.feedbackEl = this.overlay.querySelector('[data-el="feedback"]') as HTMLElement;
  }

  private attachEvents(): void {
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close();
    });

    const closeBtn = this.overlay.querySelector('[data-action="close"]') as HTMLButtonElement;
    closeBtn.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay.style.display === 'flex') this.close();
    });

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });
  }

  public async open(webinar: Webinar): Promise<void> {
    this.titleEl.textContent = webinar.title;
    this.dateEl.textContent = `${webinar.dateLabel}, ${webinar.timeLabel}`;
    this.audienceEl.textContent = webinar.audience;
    this.webinarIdInput.value = webinar.id;

    this.resetFeedback();
    this.form.reset();
    this.webinarIdInput.value = webinar.id;

    this.overlay.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    await loadRecaptchaScript();
    this.renderRecaptcha();
  }

  public close(): void {
    this.overlay.style.display = 'none';
    document.body.style.overflow = '';
  }

  private renderRecaptcha(): void {
    const container = this.overlay.querySelector('[data-el="recaptcha"]') as HTMLElement;
    if (!container || !window.grecaptcha || !RECAPTCHA_SITE_KEY) return;

    if (this.recaptchaWidgetId !== null) {
      try {
        window.grecaptcha.reset(this.recaptchaWidgetId);
        return;
      } catch {
        // Re-render below if reset fails
      }
    }

    container.innerHTML = '';
    try {
      this.recaptchaWidgetId = window.grecaptcha.render(container, {
        sitekey: RECAPTCHA_SITE_KEY,
      });
    } catch (err) {
      console.error('Erreur rendu reCAPTCHA:', err);
    }
  }

  private validate(): { ok: true; data: WebinarRegistrationPayload } | { ok: false; error: string } {
    const fd = new FormData(this.form);
    const get = (k: string) => ((fd.get(k) as string) || '').trim();

    const webinarId = get('webinarId');
    const firstName = get('firstName');
    const lastName = get('lastName');
    const email = get('email');
    const phone = get('phone');
    const company = get('company');
    const position = get('position');

    if (!webinarId) return { ok: false, error: 'Webinaire non sélectionné.' };
    if (firstName.length < 2) return { ok: false, error: 'Prénom requis.' };
    if (lastName.length < 2) return { ok: false, error: 'Nom requis.' };
    if (!EMAIL_REGEX.test(email)) return { ok: false, error: 'Adresse email invalide.' };
    if (company.length < 2) return { ok: false, error: 'Entreprise requise.' };
    if (position.length < 2) return { ok: false, error: 'Fonction requise.' };

    let recaptchaToken = '';
    try {
      recaptchaToken =
        this.recaptchaWidgetId !== null && window.grecaptcha
          ? window.grecaptcha.getResponse(this.recaptchaWidgetId)
          : '';
    } catch {
      recaptchaToken = '';
    }
    if (!recaptchaToken) return { ok: false, error: 'Merci de valider le reCAPTCHA.' };

    return {
      ok: true,
      data: { webinarId, firstName, lastName, email, phone, company, position, recaptchaToken },
    };
  }

  private async handleSubmit(): Promise<void> {
    if (this.isSubmitting) return;
    this.resetFeedback();

    const result = this.validate();
    if (!result.ok) {
      this.showFeedback(result.error, 'error');
      return;
    }

    this.isSubmitting = true;
    this.submitBtn.disabled = true;
    this.submitBtn.textContent = 'Inscription en cours...';

    try {
      const response = await fetch(`${API_URL}/api/webinar/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(result.data),
      });

      const json = await response.json().catch(() => ({}));

      if (!response.ok) {
        const msg = json?.error || json?.details || 'Une erreur est survenue. Réessayez.';
        throw new Error(msg);
      }

      this.showFeedback(
        "Inscription confirmée ! Vous allez recevoir le lien Zoom par email d'ici quelques minutes. Pensez à vérifier vos spams.",
        'success'
      );
      this.form.reset();
      if (this.recaptchaWidgetId !== null && window.grecaptcha) {
        try { window.grecaptcha.reset(this.recaptchaWidgetId); } catch { /* noop */ }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Une erreur est survenue.';
      this.showFeedback(msg, 'error');
      if (this.recaptchaWidgetId !== null && window.grecaptcha) {
        try { window.grecaptcha.reset(this.recaptchaWidgetId); } catch { /* noop */ }
      }
    } finally {
      this.isSubmitting = false;
      this.submitBtn.disabled = false;
      this.submitBtn.textContent = "Je m'inscris gratuitement";
    }
  }

  private showFeedback(message: string, kind: 'success' | 'error'): void {
    this.feedbackEl.textContent = message;
    this.feedbackEl.classList.remove('hidden', 'bg-red-50', 'text-red-700', 'bg-green-50', 'text-green-700');
    if (kind === 'success') {
      this.feedbackEl.classList.add('bg-green-50', 'text-green-700');
    } else {
      this.feedbackEl.classList.add('bg-red-50', 'text-red-700');
    }
  }

  private resetFeedback(): void {
    this.feedbackEl.classList.add('hidden');
    this.feedbackEl.textContent = '';
  }
}

let singleton: WebinarRegistrationModal | null = null;

export function getWebinarRegistrationModal(): WebinarRegistrationModal {
  if (!singleton) singleton = new WebinarRegistrationModal();
  return singleton;
}

export default WebinarRegistrationModal;
